import { useCallback, useState, type FC, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import AddCommentOutlined from "@mui/icons-material/AddCommentOutlined";
import AutoAwesomeRounded from "@mui/icons-material/AutoAwesomeRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import type { usePartnerAssistant } from "../model/usePartnerAssistant";
import type { BlockedReason } from "../model/types";
import { AssistantFeed } from "./AssistantFeed";

type Assistant = ReturnType<typeof usePartnerAssistant>;

const BLOCKED_KEYS: Record<BlockedReason, string> = {
  DISABLED: "partnerAssistant.blocked.disabled",
  BUDGET: "partnerAssistant.blocked.budget",
  DAILY_LIMIT: "partnerAssistant.blocked.dailyLimit",
};

export interface AssistantPanelProps {
  assistant: Assistant;
  onClose: () => void;
  /** Поле ввода внизу панели. */
  composer: ReactNode;
}

/**
 * Содержимое панели помощника (п. 9 спеки), общее для боковой панели
 * десктопа и листа на мобиле: заголовок с подписью раздела, «Новый диалог»
 * и «Закрыть», лента, поле ввода внизу.
 */
export const AssistantPanel: FC<AssistantPanelProps> = ({
  assistant,
  onClose,
  composer,
}) => {
  const { t } = useTranslation();
  const { state, page, send, executePlan, cancelPlan, newConversation } =
    assistant;
  const [cancelFailed, setCancelFailed] = useState(false);
  const caption = page.section
    ? page.entityTitle
      ? t("partnerAssistant.contextSectionEntity", {
          section: page.section,
          entity: page.entityTitle,
        })
      : t("partnerAssistant.contextSection", { section: page.section })
    : null;

  // Стабильные обработчики: элементы ленты мемоизированы и не
  // перерисовываются на каждый кусок потока.
  const onCancel = useCallback(
    async (planId: string) => {
      setCancelFailed(false);
      const ok = await cancelPlan(planId);
      setCancelFailed(!ok);
    },
    [cancelPlan]
  );
  const onOption = useCallback(
    (option: string) => {
      setCancelFailed(false);
      void send(option, []);
    },
    [send]
  );
  const onExecute = useCallback(
    (planId: string, steps: number[]) => {
      setCancelFailed(false);
      void executePlan(planId, steps);
    },
    [executePlan]
  );

  return (
    <Stack sx={{ height: "100%", minHeight: 0 }}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{
          pl: 2,
          pr: 1,
          py: 1,
          minHeight: 56,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <AutoAwesomeRounded color="primary" fontSize="small" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" component="h2" noWrap>
            {t("partnerAssistant.title")}
          </Typography>
          {caption && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              component="div"
            >
              {caption}
            </Typography>
          )}
        </Box>
        <Tooltip title={t("partnerAssistant.newConversation")}>
          <span>
            <IconButton
              size="small"
              aria-label={t("partnerAssistant.newConversation")}
              onClick={() => {
                setCancelFailed(false);
                newConversation();
              }}
              disabled={state.busy}
            >
              <AddCommentOutlined fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t("partnerAssistant.close")}>
          <IconButton
            size="small"
            aria-label={t("partnerAssistant.close")}
            onClick={onClose}
          >
            <CloseRounded fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 2, py: 1.5 }}>
        <AssistantFeed
          state={state}
          onOption={onOption}
          onExecute={onExecute}
          onCancel={onCancel}
        />
      </Box>
      {state.blocked && (
        <Typography
          variant="body2"
          color="warning.main"
          sx={{ px: 2, pb: 1.5 }}
        >
          {t(BLOCKED_KEYS[state.blocked])}
        </Typography>
      )}
      {state.notice === "planAction" && (
        <Typography variant="body2" color="error" sx={{ px: 2, pb: 1.5 }}>
          {t("partnerAssistant.errors.planAction")}
        </Typography>
      )}
      {cancelFailed && (
        <Typography variant="body2" color="error" sx={{ px: 2, pb: 1.5 }}>
          {t("partnerAssistant.errors.cancelPlan")}
        </Typography>
      )}
      <Box sx={{ p: 1.5, pt: 0.5, flexShrink: 0 }}>{composer}</Box>
    </Stack>
  );
};
